/**
 * SeatMap.js
 *
 * The YJ030 cabin from above, with the guest's seat in their colour. Read-only: seats aren't chosen here.
 * A 3-4-3 widebody (A B C · D E F G · H J K, no I), 30 rows. A seat outside that layout (e.g. 30Y)
 * gets its own place at the end of its row. Every other seat looks the same, so nobody else's seat is given away.
 * Sideways (nose on the left) on wide screens, upright on phones, scrolled to the guest's row.
 */

import React, { useLayoutEffect, useRef } from "react";

const CABIN_ROWS = 30;
// Lanes across the cabin, left to right (upright) or top to bottom (sideways); null is an aisle
const LANES = ["A", "B", "C", null, "D", "E", "F", "G", null, "H", "J", "K"];

const INK = "#1B2A41";
const MUTED = "#5B6577";
const SEAT = "#DDD6C8";
const CABIN = "#FBF7EF";
const MONO = "'JetBrains Mono', monospace";

/**
 * '14B' → { row: 14, letter: 'B' }
 */
const parseSeat = (seat) => {
    const match = /^(\d+)([A-Z]+)$/.exec(seat || "");
    return match ? { row: Number(match[1]), letter: match[2] } : null;
};

/**
 * @param {String} seat         -   The guest's seat, e.g. '14B'
 * @param {String} colour       -   The guest's colour
 * @param {Boolean} sideways    -   Nose on the left (wide screens) instead of at the top
 * @returns {JSX.Element}
 */
const SeatMap = ({ seat, colour, sideways }) => {
    const scrollRef = useRef(null);
    const yourSeatRef = useRef(null);
    const yours = parseSeat(seat);
    // A seat letter that isn't in the layout gets an extra lane after a gap, only used by that row
    const extra = yours && !LANES.includes(yours.letter) ? yours.letter : null;
    // The cabin grows if a seat is further back than row 30
    const rowCount = Math.max(CABIN_ROWS, yours?.row || 0);
    const lanes = extra ? [...LANES, null, extra] : LANES;

    // Bring the guest's seat into view inside the cabin (without scrolling the page)
    useLayoutEffect(() => {
        const box = scrollRef.current;
        const target = yourSeatRef.current;
        if (!box || !target) return;
        if (sideways) box.scrollLeft = target.offsetLeft - box.clientWidth / 2;
        else box.scrollTop = target.offsetTop - box.clientHeight / 2;
    }, [sideways, seat]);

    const laneSize = (lane) => (lane === null ? "0.55fr" : "1fr");
    // Upright: a column per lane and a row per seat row. Sideways: the same grid turned on its side.
    const grid = sideways
        ? { gridTemplateRows: `auto ${lanes.map(laneSize).join(" ")}`, gridTemplateColumns: `repeat(${rowCount + 1}, 26px)`, height: lanes.length * 24 + 24 }
        : { gridTemplateColumns: `1.4fr ${lanes.map(laneSize).join(" ")}`, gridTemplateRows: `auto repeat(${rowCount}, auto)` };
    // Where a cell goes: lane i of seat row r (row 0 is the letters, lane -1 is the row numbers)
    const place = (row, lane) => (sideways ? { gridColumn: row + 1, gridRow: lane + 2 } : { gridRow: row + 1, gridColumn: lane + 2 });

    const cells = [];
    lanes.forEach((letter, lane) => {
        if (letter && (letter !== extra)) {
            cells.push(<span key={`h${lane}`} className="flex items-center justify-center text-[10px] font-bold" style={{ ...place(0, lane), color: MUTED, fontFamily: MONO }}>{letter}</span>);
        }
    });
    for (let row = 1; row <= rowCount; row++) {
        cells.push(
            <span key={`r${row}`} className="flex items-center justify-center text-[10px] font-bold" style={{ ...place(row, -1), color: MUTED, fontFamily: MONO }}>{row}</span>
        );
        lanes.forEach((letter, lane) => {
            if (!letter || (letter === extra && row !== yours.row)) return;
            const isYours = yours && row === yours.row && letter === yours.letter;
            cells.push(
                <span
                    key={`${row}${letter}`}
                    ref={isYours ? yourSeatRef : undefined}
                    className="relative flex items-center justify-center m-[2px] text-[10px] font-bold"
                    style={{
                        ...place(row, lane),
                        aspectRatio: "1",
                        borderRadius: sideways ? "6px 3px 3px 6px" : "6px 6px 3px 3px",
                        background: isYours ? colour : SEAT,
                        color: isYours ? CABIN : "transparent",
                        boxShadow: isYours ? `0 0 0 3px ${CABIN}, 0 0 0 5px ${colour}, 0 6px 16px rgba(27,42,65,0.35)` : "none",
                        zIndex: isYours ? 1 : 0,
                    }}
                    aria-label={isYours ? `Your seat, ${seat}` : undefined}
                >
                    {isYours ? letter : ""}
                </span>
            );
        });
    }

    return (
        <div className="flex flex-col gap-3 w-full min-w-0">
            <div
                ref={scrollRef}
                className={sideways ? "overflow-x-auto overflow-y-hidden" : "overflow-y-auto overflow-x-hidden"}
                // Short enough on phones to stay clear of the fixed Continue bar
                style={sideways ? {} : { maxHeight: "min(46vh, 520px)" }}
            >
                {/* The cabin: rounded nose at the front, then the seats */}
                <div
                    className="relative mx-auto"
                    style={{
                        background: CABIN,
                        border: `2px solid ${INK}1f`,
                        boxShadow: "0 10px 30px rgba(27,42,65,0.12)",
                        ...(sideways
                            ? { borderRadius: "140px 24px 24px 140px", padding: "18px 24px 18px 110px", width: "max-content" }
                            : { borderRadius: "48% 48% 24px 24px / 120px 120px 24px 24px", padding: "96px 14px 20px", maxWidth: 380 }),
                    }}
                >
                    <span
                        className="absolute text-[10px] font-bold tracking-[0.3em]"
                        style={{ color: MUTED, ...(sideways ? { left: 34, top: "50%", transform: "translateY(-50%) rotate(-90deg)" } : { top: 52, left: "50%", transform: "translateX(-50%)" }) }}
                    >
                        FRONT
                    </span>
                    <div className="grid" role="img" aria-label={`Cabin seat map. Your seat is ${seat}.`} style={grid}>{cells}</div>
                </div>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-4 text-xs" style={{ color: MUTED }}>
                <span className="inline-flex items-center gap-1.5"><span className="h-3.5 w-3.5 rounded" style={{ background: colour }} /> Your seat</span>
                <span className="inline-flex items-center gap-1.5"><span className="h-3.5 w-3.5 rounded" style={{ background: SEAT }} /> Other passengers</span>
            </div>
        </div>
    );
};

export default SeatMap;
